import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { fmtDate } from "./shared";
import { UserPlus, Copy, RefreshCw, Trash2, Lock, Users, Globe, Settings2, CheckCircle2, Mail } from "lucide-react";

const VISIBILITY_OPTIONS = [
    { id: "assigned", icon: Lock, label: "Solo i lead assegnati", desc: "Ogni collaboratore vede e modifica soltanto i lead assegnati a lui." },
    { id: "view_edit_own", icon: Users, label: "Tutti i lead, modifica solo i propri", desc: "Visibilità completa, ma ogni collaboratore modifica solo i propri lead.", recommended: true },
    { id: "full", icon: Globe, label: "Tutti i lead con pieni permessi", desc: "Tutti vedono e modificano tutto, come il titolare." },
    { id: "custom", icon: Settings2, label: "Personalizzato", desc: "Imposta permessi avanzati per ogni singolo collaboratore." },
];

export default function CrmTeam() {
    const [data, setData] = useState(null);
    const [perf, setPerf] = useState(null);
    const [form, setForm] = useState({ name: "", email: "" });
    const [busy, setBusy] = useState(false);
    const [feedback, setFeedback] = useState(null);
    const [inviteLink, setInviteLink] = useState(null);
    const [copied, setCopied] = useState(false);

    const load = async () => {
        try {
            const [t, p] = await Promise.all([api.get("/crm/team"), api.get("/crm/team/performance")]);
            setData(t.data);
            setPerf(p.data);
        } catch { setData({ error: true }); }
    };
    useEffect(() => { load(); }, []);

    const invite = async () => {
        if (!form.name.trim() || !form.email.trim()) return;
        setBusy(true); setFeedback(null); setInviteLink(null);
        try {
            const r = await api.post("/crm/team/invite", form);
            setForm({ name: "", email: "" });
            setInviteLink(r.data.invite_link);
            setFeedback({ ok: true, text: r.data.email_sent ? "Invito inviato via email." : "Invito creato. Condividi il link qui sotto." });
            await load();
        } catch (e) {
            setFeedback({ ok: false, text: e?.response?.data?.detail || "Errore nell'invio dell'invito" });
        } finally { setBusy(false); }
    };

    const resend = async (id) => {
        try {
            const r = await api.post(`/crm/team/${id}/resend`);
            setInviteLink(r.data.invite_link);
            setFeedback({ ok: true, text: r.data.email_sent ? "Invito reinviato via email." : "Link invito rigenerato qui sotto." });
        } catch (e) { console.error(e); }
    };

    const remove = async (id) => {
        if (!window.confirm("Rimuovere questo collaboratore? Perderà immediatamente l'accesso al CRM.")) return;
        await api.delete(`/crm/team/${id}`);
        await load();
    };

    const setVisibility = async (v) => {
        setData((d) => ({ ...d, settings: { ...d.settings, lead_visibility: v } }));
        try { await api.put("/crm/team/settings", { lead_visibility: v }); } catch (e) { console.error(e); }
    };

    const setPerm = async (m, key, val) => {
        const perms = { ...(m.permissions || { view_all: true, edit_all: false, can_delete: true }), [key]: val };
        setData((d) => ({ ...d, members: d.members.map((x) => x.id === m.id ? { ...x, permissions: perms } : x) }));
        try { await api.put(`/crm/team/${m.id}/permissions`, perms); } catch (e) { console.error(e); }
    };

    const copyLink = () => {
        navigator.clipboard.writeText(inviteLink);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    if (!data) return <div className="text-ink-400 text-sm py-12 text-center">Caricamento team...</div>;
    if (data.error) return <div className="text-ink-400 text-sm py-12 text-center">Sezione riservata al titolare dell'agenzia.</div>;

    const visibility = data.settings?.lead_visibility || "view_edit_own";
    const members = data.members || [];

    return (
        <div data-testid="crm-team" className="space-y-10">
            {/* Invite */}
            <div className="border border-white/10 bg-ink-900/40 p-6">
                <div className="flex items-center gap-2 mb-1"><UserPlus size={16} className="text-gold" /><h3 className="font-serif text-lg text-ink-50">Invita un collaboratore</h3></div>
                <p className="text-xs text-ink-400 mb-4">Il collaboratore riceve un link personale via email, sceglie le proprie credenziali e accede al CRM dell'agenzia.</p>
                <div className="flex flex-wrap gap-2">
                    <input data-testid="team-invite-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                        placeholder="Nome agente" className="flex-1 min-w-[160px] bg-transparent border border-white/15 px-3 py-2.5 text-sm text-ink-50 focus:border-gold outline-none" />
                    <input data-testid="team-invite-email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                        placeholder="email@esempio.it" className="flex-1 min-w-[200px] bg-transparent border border-white/15 px-3 py-2.5 text-sm text-ink-50 focus:border-gold outline-none" />
                    <button data-testid="team-invite-send" onClick={invite} disabled={busy || !form.name.trim() || !form.email.trim()}
                        className="cta-gold px-6 py-2.5 text-xs disabled:opacity-40">{busy ? "Invio..." : "Invita"}</button>
                </div>
                {feedback && (
                    <div data-testid="team-invite-feedback" className={`mt-3 text-xs ${feedback.ok ? "text-green-300" : "text-red-300"}`}>{feedback.text}</div>
                )}
                {inviteLink && (
                    <div className="mt-3 flex items-center gap-2 border border-gold/30 bg-gold/5 px-3 py-2">
                        <span data-testid="team-invite-link" className="flex-1 text-[11px] text-ink-100 truncate">{inviteLink}</span>
                        <button data-testid="team-invite-copy" onClick={copyLink} className="text-gold hover:text-ink-50 flex items-center gap-1 text-[11px]">
                            {copied ? <CheckCircle2 size={13} /> : <Copy size={13} />}{copied ? "Copiato" : "Copia"}
                        </button>
                    </div>
                )}
            </div>

            {/* Visibility settings */}
            <div>
                <h3 className="font-serif text-lg text-ink-50 mb-1">Visibilità dei lead</h3>
                <p className="text-xs text-ink-400 mb-4">Scegli cosa possono vedere e modificare i collaboratori.</p>
                <div className="grid md:grid-cols-2 gap-3">
                    {VISIBILITY_OPTIONS.map((o) => {
                        const Icon = o.icon;
                        const active = visibility === o.id;
                        return (
                            <button key={o.id} data-testid={`visibility-${o.id}`} onClick={() => setVisibility(o.id)}
                                className={`text-left p-4 border transition-colors ${active ? "border-gold bg-gold/5" : "border-white/10 bg-ink-900/40 hover:border-white/25"}`}>
                                <div className="flex items-center gap-2">
                                    <Icon size={15} className={active ? "text-gold" : "text-ink-400"} />
                                    <span className={`text-sm ${active ? "text-ink-50" : "text-ink-100"}`}>{o.label}</span>
                                    {o.recommended && <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 bg-green-900/30 text-green-300 border border-green-700/40">Consigliato</span>}
                                    {active && <CheckCircle2 size={14} className="text-gold ml-auto shrink-0" />}
                                </div>
                                <p className="text-[11px] text-ink-400 mt-1.5">{o.desc}</p>
                            </button>
                        );
                    })}
                </div>
            </div>

            {/* Members list */}
            <div>
                <h3 className="font-serif text-lg text-ink-50 mb-4">Collaboratori ({members.length})</h3>
                {members.length === 0 && <p className="text-xs text-ink-500">Nessun collaboratore. Invita il primo agente qui sopra.</p>}
                <div className="space-y-2">
                    {members.map((m) => (
                        <div key={m.id} data-testid={`team-member-${m.id}`} className="border border-white/10 bg-ink-900/40 p-4">
                            <div className="flex items-center gap-3 flex-wrap">
                                <div className="w-9 h-9 rounded-full bg-gold/10 border border-gold/30 flex items-center justify-center text-gold text-sm font-serif">
                                    {(m.name || "?").charAt(0).toUpperCase()}
                                </div>
                                <div className="flex-1 min-w-[160px]">
                                    <div className="text-sm text-ink-50">{m.name}</div>
                                    <div className="text-[11px] text-ink-400 flex items-center gap-1"><Mail size={10} />{m.email}</div>
                                </div>
                                {m.status === "active"
                                    ? <span className="text-[9px] uppercase tracking-wider px-2 py-1 bg-green-900/30 text-green-300 border border-green-700/40">Attivo</span>
                                    : <span className="text-[9px] uppercase tracking-wider px-2 py-1 bg-amber-900/30 text-amber-300 border border-amber-700/40">Invitato</span>}
                                {m.status === "invited" && (
                                    <button data-testid={`team-resend-${m.id}`} onClick={() => resend(m.id)} title="Reinvia invito"
                                        className="text-ink-400 hover:text-gold"><RefreshCw size={14} /></button>
                                )}
                                <button data-testid={`team-remove-${m.id}`} onClick={() => remove(m.id)} title="Rimuovi"
                                    className="text-ink-400 hover:text-red-300"><Trash2 size={14} /></button>
                            </div>
                            {visibility === "custom" && (
                                <div className="mt-3 pt-3 border-t border-white/5 flex flex-wrap gap-4 text-[11px]">
                                    <PermToggle testid={`perm-view-${m.id}`} label="Vede tutti i lead" checked={(m.permissions || {}).view_all !== false} onChange={(v) => setPerm(m, "view_all", v)} />
                                    <PermToggle testid={`perm-edit-${m.id}`} label="Modifica tutti i lead" checked={!!(m.permissions || {}).edit_all} onChange={(v) => setPerm(m, "edit_all", v)} />
                                    <PermToggle testid={`perm-delete-${m.id}`} label="Può eliminare" checked={(m.permissions || {}).can_delete !== false} onChange={(v) => setPerm(m, "can_delete", v)} />
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            </div>

            {/* Performance */}
            <div>
                <h3 className="font-serif text-lg text-ink-50 mb-1">Performance per agente</h3>
                <p className="text-xs text-ink-400 mb-4">Statistiche calcolate sui lead assegnati a ciascun agente.</p>
                {(perf?.rows || []).length === 0
                    ? <p className="text-xs text-ink-500">Nessun dato: assegna i lead agli agenti per vedere le statistiche.</p>
                    : (
                        <div className="overflow-x-auto border border-white/10">
                            <table data-testid="team-performance-table" className="w-full text-xs">
                                <thead>
                                    <tr className="text-left text-[10px] uppercase tracking-wider text-ink-400 border-b border-white/10 bg-ink-900/60">
                                        <th className="px-4 py-3">Agente</th>
                                        <th className="px-3 py-3">Lead</th>
                                        <th className="px-3 py-3">Acquirenti</th>
                                        <th className="px-3 py-3">Venditori</th>
                                        <th className="px-3 py-3">In corso</th>
                                        <th className="px-3 py-3">Clienti</th>
                                        <th className="px-3 py-3">Conversione</th>
                                        <th className="px-3 py-3">Nuovi 30gg</th>
                                        <th className="px-3 py-3">Ultima attività</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {perf.rows.map((r) => (
                                        <tr key={r.agent || "non-assegnati"} className="border-b border-white/5 text-ink-100">
                                            <td className="px-4 py-2.5">
                                                {r.is_unassigned ? <span className="text-ink-500 italic">Non assegnati</span> : <span className="text-ink-50">{r.agent}</span>}
                                                {r.email && <div className="text-[10px] text-ink-500">{r.email}</div>}
                                            </td>
                                            <td className="px-3 py-2.5">{r.total}</td>
                                            <td className="px-3 py-2.5">{r.buyers}</td>
                                            <td className="px-3 py-2.5">{r.sellers}</td>
                                            <td className="px-3 py-2.5">{r.in_progress}</td>
                                            <td className="px-3 py-2.5 text-green-300">{r.clients}</td>
                                            <td className="px-3 py-2.5 text-gold">{r.conversion_pct}%</td>
                                            <td className="px-3 py-2.5">{r.new_30d}</td>
                                            <td className="px-3 py-2.5 text-ink-400">{r.last_activity ? fmtDate(r.last_activity) : "—"}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
            </div>
        </div>
    );
}

function PermToggle({ label, checked, onChange, testid }) {
    return (
        <label className="flex items-center gap-2 cursor-pointer text-ink-100">
            <input data-testid={testid} type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} className="accent-[#d4af37]" />
            {label}
        </label>
    );
}
